import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Target, TrendingUp, TrendingDown, Download, RefreshCw, Loader2,
  User, AlertTriangle, CheckCircle2, XCircle, Award, BookOpen,
  MessageSquare, Activity, Filter, Eye, Phone, Settings, Save, ShieldAlert,
  Plus, Copy, Trash2, Star, Users, Building, Briefcase, Calendar
} from 'lucide-react';
import { 
  getCompanyKPI, 
  calculateAllKPIs, 
  exportKPIReport, 
  getKPIFilters,
  getKPIConfig,
  updateKPIConfig,
  getKPIPenaltyTypes,
  getDepartments,
  getRoles,
  getUsers,
  getKPIConfigs,
  createKPIConfig,
  updateKPIConfigById,
  deleteKPIConfigById,
  duplicateKPIConfig,
  setKPIConfigDefault,
  getKPIAssignments,
  createKPIAssignment,
  createBulkKPIAssignments,
  deleteKPIAssignment
} from '@/lib/api';
import { toast } from 'sonner';
import ConfigHistoryPanel from '@/components/ConfigHistoryPanel';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const getScoreColor = (score) => {
  if (score >= 8) return 'text-emerald-600';
  if (score >= 6) return 'text-blue-600';
  if (score >= 4) return 'text-amber-600';
  return 'text-red-600';
};

const getScoreBadge = (score) => {
  if (score >= 8) return 'bg-emerald-500';
  if (score >= 6) return 'bg-blue-500';
  if (score >= 4) return 'bg-amber-500';
  return 'bg-red-500';
};

export default function AdminKPI() {
  // Handle both legacy admin and SaaS tenant contexts
  const outletContext = useOutletContext() || {};
  const { users: outletUsers } = outletContext;
  const [activeTab, setActiveTab] = useState('report');
  const [isLoading, setIsLoading] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  
  const [kpiData, setKpiData] = useState(null);
  const [penaltyTypes, setPenaltyTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  
  // Multi-config state
  const [configs, setConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // New config form
  const [newConfigName, setNewConfigName] = useState('');
  const [newConfigDescription, setNewConfigDescription] = useState('');
  
  // Assignment form
  const [assignmentType, setAssignmentType] = useState('user');
  const [assignmentId, setAssignmentId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().split('T')[0]);
  const [recalculateBackward, setRecalculateBackward] = useState(false);
  const [bulkAssignmentIds, setBulkAssignmentIds] = useState([]);
  
  // Filters
  const now = new Date();
  const [filters, setFilters] = useState({
    month: now.getMonth() + 1,
    year: now.getFullYear(),
    department_id: 'all',
    location: 'all',
    role_id: 'all',
    designation: 'all'
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'report') {
      loadKPIData();
    }
  }, [filters, activeTab]);

  useEffect(() => {
    // When selected config changes, update the form
    if (selectedConfigId && configs.length > 0) {
      const config = configs.find(c => c.id === selectedConfigId);
      setSelectedConfig(config || null);
    }
  }, [selectedConfigId, configs]);

  const loadInitialData = async () => {
    try {
      const [deptRes, roleRes, filtersRes, penaltyRes, usersRes] = await Promise.all([
        getDepartments(),
        getRoles(),
        getKPIFilters(),
        getKPIPenaltyTypes(),
        getUsers()
      ]);
      setDepartments(deptRes.data);
      setRoles(roleRes.data);
      setFilterOptions(filtersRes.data);
      setPenaltyTypes(penaltyRes.data);
      setUsers(usersRes.data);
      
      // Load multi-config data - force select default on initial load
      await loadConfigs(true);
      await loadAssignments();
    } catch (error) {
      console.error('Failed to load initial data:', error);
    }
  };

  const loadConfigs = async (forceSelectDefault = false) => {
    try {
      const res = await getKPIConfigs();
      setConfigs(res.data.configs || []);
      setNameSuggestions(res.data.name_suggestions || []);
      
      // Select default config on initial load or when forced
      if ((!selectedConfigId || forceSelectDefault) && res.data.configs?.length > 0) {
        const defaultConfig = res.data.configs.find(c => c.is_default) || res.data.configs[0];
        setSelectedConfigId(defaultConfig.id);
        setSelectedConfig(defaultConfig);
      }
    } catch (error) {
      console.error('Failed to load configs:', error);
    }
  };

  const loadAssignments = async () => {
    try {
      const res = await getKPIAssignments();
      setAssignments(res.data.assignments || []);
    } catch (error) {
      console.error('Failed to load assignments:', error);
    }
  };

  const loadKPIData = async () => {
    setIsLoading(true);
    try {
      const params = {
        month: filters.month,
        year: filters.year
      };
      if (filters.department_id !== 'all') params.department_id = filters.department_id;
      if (filters.location !== 'all') params.location = filters.location;
      if (filters.role_id !== 'all') params.role_id = filters.role_id;
      if (filters.designation !== 'all') params.designation = filters.designation;
      
      const res = await getCompanyKPI(params);
      setKpiData(res.data);
    } catch (error) {
      console.error('Failed to load KPI data:', error);
      toast.error('Failed to load KPI data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCalculateAll = async () => {
    setIsCalculating(true);
    try {
      const res = await calculateAllKPIs(filters.month, filters.year);
      toast.success(res.data.message);
      await loadKPIData();
    } catch (error) {
      toast.error('Failed to calculate KPIs');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = {
        month: filters.month,
        year: filters.year
      };
      if (filters.department_id !== 'all') params.department_id = filters.department_id;
      
      const res = await exportKPIReport(params);
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `KPI_Report_${MONTHS[filters.month - 1]}_${filters.year}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('KPI report exported successfully');
    } catch (error) {
      toast.error('Failed to export KPI report');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCreateConfig = async () => {
    if (!newConfigName.trim()) {
      toast.error('Please enter a configuration name');
      return;
    }
    
    try {
      const res = await createKPIConfig({
        name: newConfigName,
        description: newConfigDescription,
        is_default: configs.length === 0
      });
      toast.success('Configuration created');
      setShowCreateDialog(false);
      setNewConfigName('');
      setNewConfigDescription('');
      await loadConfigs();
      setSelectedConfigId(res.data.config.id);
    } catch (error) {
      toast.error('Failed to create configuration');
    }
  };

  const handleDuplicateConfig = async () => {
    if (!newConfigName.trim() || !selectedConfigId) return;
    
    try {
      const res = await duplicateKPIConfig(selectedConfigId, newConfigName);
      toast.success('Configuration duplicated');
      setShowDuplicateDialog(false);
      setNewConfigName('');
      await loadConfigs();
      setSelectedConfigId(res.data.config.id);
    } catch (error) {
      toast.error('Failed to duplicate configuration');
    }
  };

  const handleDeleteConfig = async (configId) => {
    const config = configs.find(c => c.id === configId);
    if (config?.is_default) {
      toast.error('Cannot delete the default configuration');
      return;
    }
    
    if (!confirm('Are you sure you want to delete this configuration? All assignments will be removed.')) return;
    
    try {
      await deleteKPIConfigById(configId);
      toast.success('Configuration deleted');
      await loadConfigs();
      await loadAssignments();
      if (selectedConfigId === configId) {
        setSelectedConfigId(configs[0]?.id || null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete configuration');
    }
  };

  const handleSetDefault = async (configId) => {
    try {
      await setKPIConfigDefault(configId);
      toast.success('Default configuration updated');
      // Update local state immediately to reflect the new default
      setSelectedConfigId(configId);
      await loadConfigs();
    } catch (error) {
      toast.error('Failed to update default');
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedConfig) return;
    
    setIsSavingConfig(true);
    try {
      await updateKPIConfigById(selectedConfigId, selectedConfig);
      toast.success('Configuration saved');
      await loadConfigs();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleCreateAssignment = async () => {
    if (!assignmentId || !selectedConfigId) {
      toast.error('Please select an assignment target');
      return;
    }
    
    try {
      await createKPIAssignment({
        config_id: selectedConfigId,
        assignment_type: assignmentType,
        assignment_id: assignmentId,
        effective_from: new Date(effectiveFrom).toISOString(),
        recalculate_backward: recalculateBackward
      });
      toast.success('Assignment created');
      setShowAssignmentDialog(false);
      setAssignmentId('');
      await loadAssignments();
    } catch (error) {
      toast.error('Failed to create assignment');
    }
  };

  const handleBulkAssignment = async () => {
    if (bulkAssignmentIds.length === 0 || !selectedConfigId) {
      toast.error('Please select at least one target');
      return;
    }
    
    try {
      await createBulkKPIAssignments({
        config_id: selectedConfigId,
        assignment_type: assignmentType,
        assignment_ids: bulkAssignmentIds,
        effective_from: new Date(effectiveFrom).toISOString(),
        recalculate_backward: recalculateBackward
      });
      toast.success(`Assigned to ${bulkAssignmentIds.length} ${assignmentType}s`);
      setShowBulkAssignDialog(false);
      setBulkAssignmentIds([]);
      await loadAssignments();
    } catch (error) {
      toast.error('Failed to create bulk assignments');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    try {
      await deleteKPIAssignment(assignmentId);
      toast.success('Assignment removed');
      await loadAssignments();
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };

  const handlePenaltyToggle = (penaltyId, checked) => {
    if (!selectedConfig) return;
    const currentPenalties = selectedConfig.selected_penalties || [];
    let newPenalties;
    if (checked) {
      newPenalties = [...currentPenalties, penaltyId];
    } else {
      newPenalties = currentPenalties.filter(p => p !== penaltyId);
    }
    setSelectedConfig({...selectedConfig, selected_penalties: newPenalties});
  };

  const handleSelectAllPenalties = (severity) => {
    if (!selectedConfig) return;
    const penaltiesOfSeverity = penaltyTypes.filter(p => p.severity === severity).map(p => p.id);
    const currentPenalties = selectedConfig.selected_penalties || [];
    const allSelected = penaltiesOfSeverity.every(p => currentPenalties.includes(p));
    
    let newPenalties;
    if (allSelected) {
      newPenalties = currentPenalties.filter(p => !penaltiesOfSeverity.includes(p));
    } else {
      newPenalties = [...new Set([...currentPenalties, ...penaltiesOfSeverity])];
    }
    setSelectedConfig({...selectedConfig, selected_penalties: newPenalties});
  };

  const getAssignmentOptions = () => {
    switch (assignmentType) {
      case 'user':
        return users.map(u => ({ id: u.id, name: u.name }));
      case 'role':
        return roles.map(r => ({ id: r.id, name: r.name }));
      case 'department':
        return departments.map(d => ({ id: d.id, name: d.name }));
      case 'designation':
        return [...new Set(users.map(u => u.designation).filter(Boolean))].map(d => ({ id: d, name: d }));
      default:
        return [];
    }
  };

  const getAssignmentIcon = (type) => {
    switch (type) {
      case 'user': return <User className="h-4 w-4" />;
      case 'role': return <Briefcase className="h-4 w-4" />;
      case 'department': return <Building className="h-4 w-4" />;
      case 'designation': return <Award className="h-4 w-4" />;
      default: return null;
    }
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 1, currentYear, currentYear + 1];
  };

  if (isLoading && !kpiData && configs.length === 0) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-kpi-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <Target className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">KPI Dashboard</h1>
              <p className="text-purple-100 mt-1">
                Multi-model KPI scoring and configuration
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="secondary"
              onClick={handleCalculateAll}
              disabled={isCalculating}
              className="bg-white/20 hover:bg-white/30 text-white"
            >
              {isCalculating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Calculate All KPIs
            </Button>
            <Button 
              onClick={handleExport} 
              disabled={isExporting}
              className="bg-white text-purple-600 hover:bg-white/90"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="report">KPI Report</TabsTrigger>
          <TabsTrigger value="config">Configuration</TabsTrigger>
          <TabsTrigger value="assignments">Assignments</TabsTrigger>
          <TabsTrigger value="history">Change History</TabsTrigger>
        </TabsList>

        {/* Report Tab */}
        <TabsContent value="report" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Filters:</span>
                </div>
                
                <Select 
                  value={String(filters.month)} 
                  onValueChange={(v) => setFilters({...filters, month: parseInt(v)})}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select 
                  value={String(filters.year)} 
                  onValueChange={(v) => setFilters({...filters, year: parseInt(v)})}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {generateYearOptions().map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select 
                  value={filters.department_id} 
                  onValueChange={(v) => setFilters({...filters, department_id: v})}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select 
                  value={filters.location} 
                  onValueChange={(v) => setFilters({...filters, location: v})}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {filterOptions?.locations?.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select 
                  value={filters.role_id} 
                  onValueChange={(v) => setFilters({...filters, role_id: v})}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {roles.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setFilters({
                    ...filters,
                    department_id: 'all',
                    location: 'all',
                    role_id: 'all',
                    designation: 'all'
                  })}
                >
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary Stats */}
          {kpiData && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Users</p>
                      <p className="text-3xl font-bold">{kpiData.total_users}</p>
                    </div>
                    <User className="h-8 w-8 text-muted-foreground/70" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-purple-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Avg KPI Score</p>
                      <p className={`text-3xl font-bold ${getScoreColor(kpiData.avg_kpi)}`}>
                        {kpiData.avg_kpi}/10
                      </p>
                    </div>
                    <Target className="h-8 w-8 text-purple-300" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Call Quality</p>
                      <p className={`text-3xl font-bold ${getScoreColor(kpiData.avg_call_quality / 0.7)}`}>
                        {kpiData.avg_call_quality}/7
                      </p>
                    </div>
                    <Phone className="h-8 w-8 text-blue-300" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Homework</p>
                      <p className={`text-3xl font-bold ${getScoreColor(kpiData.avg_homework * 10)}`}>
                        {kpiData.avg_homework}/1
                      </p>
                    </div>
                    <BookOpen className="h-8 w-8 text-green-300" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className={kpiData.compliance_violations > 0 ? 'border-red-200' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Violations</p>
                      <p className={`text-3xl font-bold ${kpiData.compliance_violations > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {kpiData.compliance_violations}
                      </p>
                    </div>
                    {kpiData.compliance_violations > 0 ? (
                      <AlertTriangle className="h-8 w-8 text-red-300" />
                    ) : (
                      <CheckCircle2 className="h-8 w-8 text-emerald-300" />
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Top Performers & Needs Improvement */}
          {kpiData && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="border-emerald-200">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-emerald-700">
                    <Award className="h-5 w-5" />
                    Top Performers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {kpiData.top_performers?.length > 0 ? (
                    <div className="space-y-3">
                      {kpiData.top_performers.map((user, i) => (
                        <div 
                          key={user.user_id} 
                          className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg cursor-pointer hover:bg-emerald-100"
                          onClick={() => setSelectedUser(user)}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-bold text-emerald-600">#{i + 1}</span>
                            <div>
                              <p className="font-medium">{user.user_name}</p>
                              <p className="text-xs text-muted-foreground">{user.department_name} • {user.designation}</p>
                            </div>
                          </div>
                          <Badge className={getScoreBadge(user.total_kpi_score)}>
                            {user.total_kpi_score}/10
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No data available</p>
                  )}
                </CardContent>
              </Card>
              
              <Card className="border-amber-200">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-amber-700">
                    <TrendingDown className="h-5 w-5" />
                    Needs Improvement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {kpiData.needs_improvement?.length > 0 ? (
                    <div className="space-y-3">
                      {kpiData.needs_improvement.map((user) => (
                        <div 
                          key={user.user_id} 
                          className="flex items-center justify-between p-2 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100"
                          onClick={() => setSelectedUser(user)}
                        >
                          <div className="flex items-center gap-3">
                            {user.has_compliance_violation && (
                              <AlertTriangle className="h-4 w-4 text-red-500" />
                            )}
                            <div>
                              <p className="font-medium">{user.user_name}</p>
                              <p className="text-xs text-muted-foreground">{user.department_name} • {user.designation}</p>
                            </div>
                          </div>
                          <Badge className={getScoreBadge(user.total_kpi_score)}>
                            {user.total_kpi_score}/10
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">All users are performing well!</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Full KPI Table */}
          <Card>
            <CardHeader>
              <CardTitle>All Users KPI - {MONTHS[filters.month - 1]} {filters.year}</CardTitle>
              <CardDescription>
                Click on a row to see detailed breakdown
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-center">Call Quality (7)</TableHead>
                    <TableHead className="text-center">Homework (1)</TableHead>
                    <TableHead className="text-center">Feedback (1)</TableHead>
                    <TableHead className="text-center">Engagement (1)</TableHead>
                    <TableHead className="text-center">Total KPI</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {kpiData?.kpis?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-12 text-muted-foreground">
                        No KPI data found. Click &quot;Calculate All KPIs&quot; to generate.
                      </TableCell>
                    </TableRow>
                  ) : (
                    kpiData?.kpis?.map((kpi) => (
                      <TableRow 
                        key={kpi.user_id} 
                        className={`cursor-pointer hover:bg-muted/50 ${kpi.has_compliance_violation ? 'bg-red-50' : ''}`}
                        onClick={() => setSelectedUser(kpi)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="font-medium">{kpi.user_name}</p>
                              <p className="text-xs text-muted-foreground">{kpi.designation}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{kpi.department_name || '-'}</TableCell>
                        <TableCell>{kpi.location || '-'}</TableCell>
                        <TableCell className="text-center">
                          <span className={getScoreColor(kpi.call_quality_score / 0.7)}>
                            {kpi.call_quality_score}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={getScoreColor(kpi.homework_completion_score * 10)}>
                            {kpi.homework_completion_score}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={getScoreColor(kpi.feedback_compliance_score * 10)}>
                            {kpi.feedback_compliance_score}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={getScoreColor(kpi.coaching_engagement_score * 10)}>
                            {kpi.coaching_engagement_score}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={getScoreBadge(kpi.total_kpi_score)}>
                            {kpi.total_kpi_score}/10
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {kpi.has_compliance_violation ? (
                            <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                              <XCircle className="h-3 w-3" />
                              Violation
                            </Badge>
                          ) : kpi.total_kpi_score >= 8 ? (
                            <Badge className="bg-emerald-500 flex items-center gap-1 w-fit">
                              <CheckCircle2 className="h-3 w-3" />
                              Excellent
                            </Badge>
                          ) : kpi.total_kpi_score >= 6 ? (
                            <Badge className="bg-blue-500">Good</Badge>
                          ) : (
                            <Badge className="bg-amber-500">Needs Work</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-6">
          {/* Config Selector */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  KPI Configurations
                </span>
                <Button onClick={() => setShowCreateDialog(true)} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  New Configuration
                </Button>
              </CardTitle>
              <CardDescription>
                Select a configuration to view and edit, or create new configurations for different teams
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Select value={selectedConfigId || ''} onValueChange={setSelectedConfigId}>
                  <SelectTrigger className="w-[300px]">
                    <SelectValue placeholder="Select configuration" />
                  </SelectTrigger>
                  <SelectContent>
                    {configs.map(config => (
                      <SelectItem key={config.id} value={config.id}>
                        <span className="flex items-center gap-2">
                          {config.name}
                          {config.is_default && <Badge variant="outline" className="ml-2">Default</Badge>}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedConfigId && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setNewConfigName(`${selectedConfig?.name || 'Config'} Copy`);
                        setShowDuplicateDialog(true);
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Duplicate
                    </Button>
                    {!selectedConfig?.is_default && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleSetDefault(selectedConfigId)}
                        >
                          <Star className="h-4 w-4 mr-1" />
                          Set as Default
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteConfig(selectedConfigId)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Config Details */}
          {selectedConfig && (
            <>
              {/* Config Name & Description */}
              <Card>
                <CardHeader>
                  <CardTitle>Configuration Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Configuration Name</Label>
                      <Input 
                        value={selectedConfig.name || ''} 
                        onChange={(e) => setSelectedConfig({...selectedConfig, name: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input 
                        value={selectedConfig.description || ''} 
                        onChange={(e) => setSelectedConfig({...selectedConfig, description: e.target.value})}
                        placeholder="Optional description"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* KPI Weights */}
              <Card>
                <CardHeader>
                  <CardTitle>KPI Weights Configuration</CardTitle>
                  <CardDescription>
                    Configure the weights for each KPI category. Total must equal 10.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Call Quality */}
                  <div className="p-4 bg-blue-50 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Phone className="h-5 w-5 text-blue-600" />
                        <Label className="text-lg font-medium">Call Quality Score</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="10"
                          value={selectedConfig.call_quality_weight || 7}
                          onChange={(e) => setSelectedConfig({...selectedConfig, call_quality_weight: parseFloat(e.target.value) || 0})}
                          className="w-20"
                        />
                        <span className="text-muted-foreground">/ 10</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Label className="text-sm text-muted-foreground">Minimum Score Threshold (below = 0 points)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        max="10"
                        value={selectedConfig.call_quality_min_threshold || 6}
                        onChange={(e) => setSelectedConfig({...selectedConfig, call_quality_min_threshold: parseFloat(e.target.value) || 6})}
                        className="w-20"
                      />
                    </div>
                  </div>

                  {/* Homework */}
                  <div className="p-4 bg-green-50 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-green-600" />
                        <Label className="text-lg font-medium">Homework Completion</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="10"
                          value={selectedConfig.homework_completion_weight || 1}
                          onChange={(e) => setSelectedConfig({...selectedConfig, homework_completion_weight: parseFloat(e.target.value) || 0})}
                          className="w-20"
                        />
                        <span className="text-muted-foreground">/ 10</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Label className="text-sm text-muted-foreground">Minimum Completion % (below = 0 points)</Label>
                      <Input
                        type="number"
                        step="5"
                        min="0"
                        max="100"
                        value={selectedConfig.homework_min_threshold || 50}
                        onChange={(e) => setSelectedConfig({...selectedConfig, homework_min_threshold: parseFloat(e.target.value) || 50})}
                        className="w-20"
                      />
                      <span className="text-muted-foreground">%</span>
                    </div>
                  </div>

                  {/* Feedback Compliance */}
                  <div className="p-4 bg-amber-50 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-amber-600" />
                        <Label className="text-lg font-medium">Feedback Compliance</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="10"
                          value={selectedConfig.feedback_compliance_weight || 1}
                          onChange={(e) => setSelectedConfig({...selectedConfig, feedback_compliance_weight: parseFloat(e.target.value) || 0})}
                          className="w-20"
                        />
                        <span className="text-muted-foreground">/ 10</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Label className="text-sm text-muted-foreground">Minimum Compliance % (below = 0 points)</Label>
                      <Input
                        type="number"
                        step="5"
                        min="0"
                        max="100"
                        value={selectedConfig.feedback_min_threshold || 50}
                        onChange={(e) => setSelectedConfig({...selectedConfig, feedback_min_threshold: parseFloat(e.target.value) || 50})}
                        className="w-20"
                      />
                      <span className="text-muted-foreground">%</span>
                    </div>
                  </div>

                  {/* Coaching Engagement */}
                  <div className="p-4 bg-purple-50 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Activity className="h-5 w-5 text-purple-600" />
                        <Label className="text-lg font-medium">Coaching Engagement</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="10"
                          value={selectedConfig.coaching_engagement_weight || 1}
                          onChange={(e) => setSelectedConfig({...selectedConfig, coaching_engagement_weight: parseFloat(e.target.value) || 0})}
                          className="w-20"
                        />
                        <span className="text-muted-foreground">/ 10</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm text-muted-foreground">Target activities/month</Label>
                        <Input
                          type="number"
                          min="1"
                          max="100"
                          value={selectedConfig.coaching_engagement_target || 20}
                          onChange={(e) => setSelectedConfig({...selectedConfig, coaching_engagement_target: parseInt(e.target.value) || 20})}
                          className="w-20"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-sm text-muted-foreground">Min % (below = 0)</Label>
                        <Input
                          type="number"
                          step="5"
                          min="0"
                          max="100"
                          value={selectedConfig.coaching_min_threshold || 25}
                          onChange={(e) => setSelectedConfig({...selectedConfig, coaching_min_threshold: parseFloat(e.target.value) || 25})}
                          className="w-20"
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </div>
                  </div>

                  {/* Total Weight */}
                  <div className={`p-4 rounded-lg ${
                    ((selectedConfig.call_quality_weight || 0) + (selectedConfig.homework_completion_weight || 0) + 
                     (selectedConfig.feedback_compliance_weight || 0) + (selectedConfig.coaching_engagement_weight || 0)) === 10
                      ? 'bg-emerald-50 border border-emerald-200'
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Total Weight</span>
                      <span className={`text-2xl font-bold ${
                        ((selectedConfig.call_quality_weight || 0) + (selectedConfig.homework_completion_weight || 0) + 
                         (selectedConfig.feedback_compliance_weight || 0) + (selectedConfig.coaching_engagement_weight || 0)) === 10
                          ? 'text-emerald-600' : 'text-red-600'
                      }`}>
                        {((selectedConfig.call_quality_weight || 0) + (selectedConfig.homework_completion_weight || 0) + 
                          (selectedConfig.feedback_compliance_weight || 0) + (selectedConfig.coaching_engagement_weight || 0)).toFixed(1)} / 10
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Compliance Penalties */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5 text-red-500" />
                    Compliance Penalties
                  </CardTitle>
                  <CardDescription>
                    Select which violations should result in 0 KPI for the entire month
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                    <div>
                      <p className="font-medium">Enable Compliance Penalties</p>
                      <p className="text-sm text-muted-foreground">
                        When enabled, selected violations will result in 0 KPI for the month
                      </p>
                    </div>
                    <Switch
                      checked={selectedConfig.compliance_penalty_enabled}
                      onCheckedChange={(checked) => setSelectedConfig({...selectedConfig, compliance_penalty_enabled: checked})}
                    />
                  </div>
                  
                  {selectedConfig.compliance_penalty_enabled && (
                    <div className="space-y-4">
                      {['critical', 'medium', 'low'].map(severity => (
                        <div key={severity} className={`p-4 border rounded-lg ${
                          severity === 'critical' ? 'border-red-200' : 
                          severity === 'medium' ? 'border-amber-200' : 'border-slate-200'
                        }`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold flex items-center gap-2 ${
                              severity === 'critical' ? 'text-red-700' :
                              severity === 'medium' ? 'text-amber-700' : 'text-slate-700'
                            }`}>
                              <AlertTriangle className="h-4 w-4" />
                              {severity.charAt(0).toUpperCase() + severity.slice(1)} Violations
                            </h4>
                            <Button variant="ghost" size="sm" onClick={() => handleSelectAllPenalties(severity)}>
                              Toggle All
                            </Button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {penaltyTypes.filter(p => p.severity === severity).map(penalty => (
                              <div key={penalty.id} className="flex items-start space-x-3">
                                <Checkbox
                                  id={`${selectedConfigId}-${penalty.id}`}
                                  checked={(selectedConfig.selected_penalties || []).includes(penalty.id)}
                                  onCheckedChange={(checked) => handlePenaltyToggle(penalty.id, checked)}
                                />
                                <Label htmlFor={`${selectedConfigId}-${penalty.id}`} className="text-sm font-normal cursor-pointer">
                                  {penalty.label}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button onClick={handleSaveConfig} disabled={isSavingConfig}>
                  {isSavingConfig ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Configuration
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {/* Assignments Tab */}
        <TabsContent value="assignments" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Configuration Assignments
                </span>
                <div className="flex gap-2">
                  <Button onClick={() => setShowAssignmentDialog(true)} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Assignment
                  </Button>
                  <Button onClick={() => setShowBulkAssignDialog(true)} size="sm" variant="outline">
                    <Users className="h-4 w-4 mr-2" />
                    Bulk Assign
                  </Button>
                </div>
              </CardTitle>
              <CardDescription>
                Assign KPI configurations to users, roles, designations, or departments. 
                Priority: User {'>'} Role {'>'} Designation {'>'} Department {'>'} Default
              </CardDescription>
            </CardHeader>
            <CardContent>
              {assignments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No assignments yet. All users are using the default configuration.</p>
                  <Button className="mt-4" onClick={() => setShowAssignmentDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Assignment
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Configuration</TableHead>
                      <TableHead>Effective From</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignments.map(assignment => (
                      <TableRow key={assignment.id}>
                        <TableCell>
                          <Badge variant="outline" className="flex items-center gap-1 w-fit">
                            {getAssignmentIcon(assignment.assignment_type)}
                            {assignment.assignment_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          {assignment.assignment_name || assignment.assignment_id}
                        </TableCell>
                        <TableCell>
                          <Badge>{assignment.config_name}</Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(assignment.effective_from).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteAssignment(assignment.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <ConfigHistoryPanel configType="kpi" />
        </TabsContent>
      </Tabs>

      {/* Create Config Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New KPI Configuration</DialogTitle>
            <DialogDescription>
              Create a new configuration with custom KPI weights and rules
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Configuration Name</Label>
              <Input 
                value={newConfigName} 
                onChange={(e) => setNewConfigName(e.target.value)}
                placeholder="Enter configuration name"
              />
              <div className="flex flex-wrap gap-2 mt-2">
                {nameSuggestions.map(suggestion => (
                  <Badge 
                    key={suggestion} 
                    variant="outline" 
                    className="cursor-pointer hover:bg-muted"
                    onClick={() => setNewConfigName(suggestion)}
                  >
                    {suggestion}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea 
                value={newConfigDescription} 
                onChange={(e) => setNewConfigDescription(e.target.value)}
                placeholder="Describe this configuration"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateConfig}>Create Configuration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Config Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Configuration</DialogTitle>
            <DialogDescription>
              Create a copy of &quot;{selectedConfig?.name}&quot; with a new name
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Configuration Name</Label>
              <Input 
                value={newConfigName} 
                onChange={(e) => setNewConfigName(e.target.value)}
                placeholder="Enter new configuration name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>Cancel</Button>
            <Button onClick={handleDuplicateConfig}>Duplicate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={showAssignmentDialog} onOpenChange={setShowAssignmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Assignment</DialogTitle>
            <DialogDescription>
              Assign the selected KPI configuration to a user, role, designation, or department
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Configuration</Label>
              <Select value={selectedConfigId || ''} onValueChange={setSelectedConfigId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select configuration" />
                </SelectTrigger>
                <SelectContent>
                  {configs.map(config => (
                    <SelectItem key={config.id} value={config.id}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Assignment Type</Label>
              <Select value={assignmentType} onValueChange={(v) => { setAssignmentType(v); setAssignmentId(''); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">
                    <span className="flex items-center gap-2"><User className="h-4 w-4" /> User</span>
                  </SelectItem>
                  <SelectItem value="role">
                    <span className="flex items-center gap-2"><Briefcase className="h-4 w-4" /> Role</span>
                  </SelectItem>
                  <SelectItem value="designation">
                    <span className="flex items-center gap-2"><Award className="h-4 w-4" /> Designation</span>
                  </SelectItem>
                  <SelectItem value="department">
                    <span className="flex items-center gap-2"><Building className="h-4 w-4" /> Department</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Assign To</Label>
              <Select value={assignmentId} onValueChange={setAssignmentId}>
                <SelectTrigger>
                  <SelectValue placeholder={`Select ${assignmentType}`} />
                </SelectTrigger>
                <SelectContent>
                  {getAssignmentOptions().map(option => (
                    <SelectItem key={option.id} value={option.id}>{option.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Effective From</Label>
              <Input 
                type="date" 
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
            </div>
            
            {assignmentType === 'user' && (
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="recalculate" 
                  checked={recalculateBackward}
                  onCheckedChange={setRecalculateBackward}
                />
                <Label htmlFor="recalculate" className="text-sm">
                  Recalculate past KPIs with this configuration
                </Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignmentDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAssignment}>Create Assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assignment Dialog */}
      <Dialog open={showBulkAssignDialog} onOpenChange={setShowBulkAssignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Assignment</DialogTitle>
            <DialogDescription>
              Assign a configuration to multiple roles, designations, or departments at once
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Configuration</Label>
              <Select value={selectedConfigId || ''} onValueChange={setSelectedConfigId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select configuration" />
                </SelectTrigger>
                <SelectContent>
                  {configs.map(config => (
                    <SelectItem key={config.id} value={config.id}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Assignment Type</Label>
              <Select value={assignmentType} onValueChange={(v) => { setAssignmentType(v); setBulkAssignmentIds([]); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="role">
                    <span className="flex items-center gap-2"><Briefcase className="h-4 w-4" /> Roles</span>
                  </SelectItem>
                  <SelectItem value="designation">
                    <span className="flex items-center gap-2"><Award className="h-4 w-4" /> Designations</span>
                  </SelectItem>
                  <SelectItem value="department">
                    <span className="flex items-center gap-2"><Building className="h-4 w-4" /> Departments</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Select {assignmentType}s</Label>
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {getAssignmentOptions().map(option => (
                  <div key={option.id} className="flex items-center space-x-2">
                    <Checkbox 
                      id={`bulk-${option.id}`}
                      checked={bulkAssignmentIds.includes(option.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setBulkAssignmentIds([...bulkAssignmentIds, option.id]);
                        } else {
                          setBulkAssignmentIds(bulkAssignmentIds.filter(id => id !== option.id));
                        }
                      }}
                    />
                    <Label htmlFor={`bulk-${option.id}`} className="text-sm cursor-pointer">
                      {option.name}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">{bulkAssignmentIds.length} selected</p>
            </div>
            
            <div className="space-y-2">
              <Label>Effective From</Label>
              <Input 
                type="date" 
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkAssignment} disabled={bulkAssignmentIds.length === 0}>
              Assign to {bulkAssignmentIds.length} {assignmentType}s
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User KPI Detail Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-600" />
              KPI Details - {selectedUser?.user_name}
            </DialogTitle>
            <DialogDescription>
              {MONTHS[filters.month - 1]} {filters.year} • {selectedUser?.department_name} • {selectedUser?.designation}
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6">
              {/* Total Score */}
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Total KPI Score</p>
                <p className={`text-5xl font-bold ${getScoreColor(selectedUser.total_kpi_score)}`}>
                  {selectedUser.total_kpi_score}
                  <span className="text-2xl text-muted-foreground">/10</span>
                </p>
                {selectedUser.has_compliance_violation && (
                  <Badge variant="destructive" className="mt-2">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Score reduced to 0 due to compliance violation
                  </Badge>
                )}
              </div>

              {/* Score Breakdown */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Phone className="h-4 w-4 text-blue-500" />
                      Call Quality (70%)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Score</span>
                        <span className={`font-bold ${getScoreColor(selectedUser.call_quality_score / 0.7)}`}>
                          {selectedUser.call_quality_score}/7
                        </span>
                      </div>
                      <Progress value={(selectedUser.call_quality_score / 7) * 100} className="h-2" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-green-500" />
                      Homework (10%)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Score</span>
                        <span className={`font-bold ${getScoreColor(selectedUser.homework_completion_score * 10)}`}>
                          {selectedUser.homework_completion_score}/1
                        </span>
                      </div>
                      <Progress value={selectedUser.homework_completion_score * 100} className="h-2" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-amber-500" />
                      Feedback (10%)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Score</span>
                        <span className={`font-bold ${getScoreColor(selectedUser.feedback_compliance_score * 10)}`}>
                          {selectedUser.feedback_compliance_score}/1
                        </span>
                      </div>
                      <Progress value={selectedUser.feedback_compliance_score * 100} className="h-2" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Activity className="h-4 w-4 text-purple-500" />
                      Engagement (10%)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Score</span>
                        <span className={`font-bold ${getScoreColor(selectedUser.coaching_engagement_score * 10)}`}>
                          {selectedUser.coaching_engagement_score}/1
                        </span>
                      </div>
                      <Progress value={selectedUser.coaching_engagement_score * 100} className="h-2" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Compliance Violations */}
              {selectedUser.has_compliance_violation && selectedUser.compliance_violations?.length > 0 && (
                <Card className="border-red-200 bg-red-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-red-700">
                      <AlertTriangle className="h-4 w-4" />
                      Compliance Violations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-1 text-sm text-red-700">
                      {selectedUser.compliance_violations.map((v, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          {v}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
